import type { EntityATI } from '@atlassian/search-client/common/types';

/**
 * The set of possible values the universal type filter shows a user
 *
 * This is the superset. Only values that have results for a 1P/3P available to an end user will be shown
 *
 * Each connector maps nouns, subtypes into this list
 */
export const TypeFilterValueKey: {
	readonly Document: 'document';
	readonly Spreadsheet: 'spreadsheet';
	readonly Presentation: 'presentation';
	readonly Repository: 'repository';
	readonly PullRequest: 'pull-request';
	readonly Branch: 'branch';
	readonly Commit: 'commit';
	readonly WebPage: 'web-page';
	readonly Conversation: 'conversation';
	readonly Message: 'message';
	readonly Video: 'video';
	readonly Design: 'design';
	readonly Page: 'page';
	readonly Blogpost: 'blogpost';
	readonly Attachment: 'attachment';
	readonly Space: 'space';
	readonly Whiteboard: 'whiteboard';
	readonly Comment: 'comment';
	readonly Database: 'database';
	readonly Embed: 'embed';
	readonly Folder: 'folder';
	readonly Calendar: 'calendar';
	readonly SpaceCalendar: 'space-calendar';
	readonly Question: 'question';
	readonly Answer: 'answer';
	readonly Issue: 'issue';
	readonly Epic: 'epic';
	readonly Bug: 'bug';
	readonly Story: 'story';
	readonly Task: 'task';
	readonly Subtask: 'subtask';
	readonly Project: 'project';
	readonly Incident: 'incident';
	readonly Filter: 'filter';
	readonly Plan: 'plan';
	readonly Board: 'board';
	readonly Component: 'component';
	readonly Card: 'card';
	readonly Goal: 'goal';
	readonly Tag: 'tag';
	readonly WorkItem: 'work-item';
	readonly FocusArea: 'focus-area';
	readonly Ask: 'ask';
	readonly Dashboard: 'dashboard';
	readonly Invoice: 'invoice';
	readonly Deal: 'deal';
	readonly Gliffy: 'gliffy';
	readonly CustomerContact: 'customer-contact';
	readonly CustomerOrg: 'customer-org';
	readonly DataTable: 'data-table';
	readonly AssetsObject: 'object';
	readonly Type: 'type';
	readonly Schema: 'schema';
	readonly Position: 'position';
	readonly Image: 'image';
	readonly Audio: 'audio';
	readonly Pdf: 'pdf';
	readonly Shortcut: 'shortcut';
	readonly Code: 'code';
	readonly Archive: 'archive';
	readonly Other: 'other';
	readonly Notebook: 'notebook';
	readonly Form: 'form';
	readonly Article: 'article';
	readonly Post: 'post';
	readonly Ticket: 'ticket';
	readonly Campaign: 'campaign';
	readonly Case: 'case';
} = {
	Document: 'document',
	Spreadsheet: 'spreadsheet',
	Presentation: 'presentation',
	Repository: 'repository',
	PullRequest: 'pull-request',
	Branch: 'branch',
	Commit: 'commit',
	WebPage: 'web-page',
	Conversation: 'conversation',
	Message: 'message',
	Video: 'video',
	Design: 'design',
	Page: 'page',
	Blogpost: 'blogpost',
	Attachment: 'attachment',
	Space: 'space',
	Whiteboard: 'whiteboard',
	Comment: 'comment',
	Database: 'database',
	Embed: 'embed',
	Folder: 'folder',
	Calendar: 'calendar',
	SpaceCalendar: 'space-calendar',
	Question: 'question',
	Answer: 'answer',
	Issue: 'issue',
	Epic: 'epic',
	Bug: 'bug',
	Story: 'story',
	Task: 'task',
	Subtask: 'subtask',
	Project: 'project',
	Incident: 'incident',
	Filter: 'filter',
	Plan: 'plan',
	Board: 'board',
	Component: 'component',
	Card: 'card',
	Goal: 'goal',
	Tag: 'tag',
	WorkItem: 'work-item',
	FocusArea: 'focus-area',
	Ask: 'ask',
	Dashboard: 'dashboard',
	Invoice: 'invoice',
	Deal: 'deal',
	Gliffy: 'gliffy',
	CustomerContact: 'customer-contact',
	CustomerOrg: 'customer-org',
	DataTable: 'data-table',
	AssetsObject: 'object',
	Type: 'type',
	Schema: 'schema',
	Position: 'position',
	Image: 'image',
	Audio: 'audio',
	Pdf: 'pdf',
	Shortcut: 'shortcut',
	Code: 'code',
	Archive: 'archive',
	Other: 'other',
	Notebook: 'notebook',
	Form: 'form',
	Article: 'article',
	Post: 'post',
	Ticket: 'ticket',
	Campaign: 'campaign',
	Case: 'case',
} as const;
export type TypeFilterValueKey = (typeof TypeFilterValueKey)[keyof typeof TypeFilterValueKey];
export const allTypeFilterValueKeys: (
	| 'object'
	| 'project'
	| 'space'
	| 'document'
	| 'spreadsheet'
	| 'presentation'
	| 'repository'
	| 'pull-request'
	| 'branch'
	| 'commit'
	| 'web-page'
	| 'conversation'
	| 'message'
	| 'video'
	| 'design'
	| 'page'
	| 'blogpost'
	| 'attachment'
	| 'whiteboard'
	| 'comment'
	| 'database'
	| 'embed'
	| 'folder'
	| 'calendar'
	| 'space-calendar'
	| 'question'
	| 'answer'
	| 'issue'
	| 'epic'
	| 'bug'
	| 'story'
	| 'task'
	| 'subtask'
	| 'incident'
	| 'filter'
	| 'plan'
	| 'board'
	| 'component'
	| 'card'
	| 'goal'
	| 'tag'
	| 'work-item'
	| 'focus-area'
	| 'ask'
	| 'dashboard'
	| 'invoice'
	| 'deal'
	| 'gliffy'
	| 'customer-contact'
	| 'customer-org'
	| 'data-table'
	| 'type'
	| 'schema'
	| 'position'
	| 'image'
	| 'audio'
	| 'pdf'
	| 'shortcut'
	| 'code'
	| 'archive'
	| 'other'
	| 'notebook'
	| 'form'
	| 'article'
	| 'post'
	| 'ticket'
	| 'campaign'
	| 'case'
)[] = Object.values(TypeFilterValueKey);

/**
 * Subtypes used to separate nouns
 *
 * These are statically mapped to a noun below
 */
// Keep subtype additions in sync with filters/default subtype mappings to avoid broad fallback results.
export const NounSubtypeKey: {
	readonly Document: 'document';
	readonly Spreadsheet: 'spreadsheet';
	readonly Presentation: 'presentation';
	readonly File: 'file';
	readonly Database: 'database';
	readonly WebPage: 'web-page';
	readonly Page: 'page';
	readonly Blogpost: 'blogpost';
	readonly Article: 'article';
	readonly Post: 'post';
	readonly Dashboard: 'dashboard';
	readonly Invoice: 'invoice';
	readonly Design: 'design';
	readonly Deal: 'deal';
	readonly Video: 'video';
	readonly Task: 'task';
	readonly Issue: 'issue';
	readonly Space: 'space';
	readonly Project: 'project';
	readonly Repository: 'repository';
	readonly CustomerContact: 'customer-contact';
	readonly CustomerOrg: 'customer-org';
	readonly Image: 'image';
	readonly Archive: 'archive';
	readonly Audio: 'audio';
	readonly Code: 'code';
	readonly Pdf: 'pdf';
	readonly Other: 'other';
	readonly Software: 'software';
	readonly ServiceDesk: 'service_desk';
	readonly Business: 'business';
	readonly DataTable: 'data-table';
	readonly AssetsObject: 'object';
	readonly Schema: 'schema';
	readonly Type: 'type';
	readonly Folder: 'folder';
	readonly Notebook: 'notebook';
	readonly Form: 'form';
	readonly Shortcut: 'shortcut';
	readonly Site: 'site';
	readonly Subsite: 'subsite';
	readonly Drive: 'drive';
	readonly Comment: 'comment';
} = {
	Document: 'document',
	Spreadsheet: 'spreadsheet',
	Presentation: 'presentation',
	File: 'file',
	Database: 'database',
	WebPage: 'web-page',
	Page: 'page',
	Blogpost: 'blogpost',
	Article: 'article',
	Post: 'post',
	Dashboard: 'dashboard',
	Invoice: 'invoice',
	Design: 'design',
	Deal: 'deal',
	Video: 'video',
	Task: 'task',
	Issue: 'issue',
	Space: 'space',
	Project: 'project',
	Repository: 'repository',
	CustomerContact: 'customer-contact',
	CustomerOrg: 'customer-org',
	Image: 'image',
	Archive: 'archive',
	Audio: 'audio',
	Code: 'code',
	Pdf: 'pdf',
	Other: 'other',
	Software: 'software',
	ServiceDesk: 'service_desk',
	Business: 'business',
	DataTable: 'data-table',
	AssetsObject: 'object',
	Schema: 'schema',
	Type: 'type',
	Folder: 'folder',
	Notebook: 'notebook',
	Form: 'form',
	Shortcut: 'shortcut',
	Site: 'site',
	Subsite: 'subsite',
	Drive: 'drive',
	Comment: 'comment',
} as const;
export type NounSubtypeKey = (typeof NounSubtypeKey)[keyof typeof NounSubtypeKey];

export type TypeFilterValue3PEntities = { entities3P: EntityATI[]; subTypes3P: NounSubtypeKey[] };

export type TypeFilterConfig3P = Map<TypeFilterValueKey, TypeFilterValue3PEntities>;
